<template>
  <a-modal
    :title="$t('platformCollection.investor_details')"
    :width="1200"
    :visible="visible"
    :footer="null"
    @cancel="handleCancel"
  >
    <a-spin :spinning="loading">
      <div class="detail-container" v-if="detailData">
        <!-- Registration info -->
        <div class="section">
          <div class="section-title">{{ $t('platformCollection.registration_info') }}</div>
          <div class="section-content">
            <a-row :gutter="[16, 16]">
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.investor_id') }}:</span>
                <span class="value">{{ detailData.id || '-' }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.account') }}:</span>
                <span class="value">{{ detailData.mobile || '-' }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.status') }}:</span>
                <span class="value">{{
                  detailData.status === 1 ? $t('platformCollection.normal') : $t('platformCollection.disabled')
                }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.register_time') }}:</span>
                <span class="value">{{ detailData.registerTime || '-' }}</span>
              </a-col>
            </a-row>
          </div>
        </div>

        <!-- Account opening info -->
        <div class="section">
          <div class="section-title">{{ $t('platformCollection.account_info') }}</div>
          <div class="section-content">
            <a-row :gutter="[16, 24]">
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.name') }}:</span>
                <span class="value">{{ detailData.name || '-' }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.gender') }}:</span>
                <span class="value">{{ formatGender(detailData.sex) }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.id_card_number') }}:</span>
                <span class="value">{{ detailData.idCardNum || '-' }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.birthday') }}:</span>
                <span class="value">{{ detailData.birthday || '-' }}</span>
              </a-col>
               <a-col :span="6">
                <span class="label">{{ $t('platformCollection.city') }}:</span>
                <span class="value">{{ detailData.home || '-' }}</span>
              </a-col>

              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.address') }}:</span>
                <span class="value">{{ detailData.resident || '-' }}</span>
              </a-col>
              <a-col :span="12">
                <div class="img-wrapper">
                  <span class="label">{{ $t('platformCollection.id_card_photo') }}:</span>
                  <div class="img-box-container">
                    <div class="img-box" v-if="detailData.idCardFront">
                      <img :src="detailData.idCardFront" @click="handlePreview(detailData.idCardFront)" />
                      <span>{{ $t('platformCollection.front') }}</span>
                    </div>
                    <div class="img-box" v-if="detailData.idCardBack">
                      <img :src="detailData.idCardBack" @click="handlePreview(detailData.idCardBack)" />
                      <span>{{ $t('platformCollection.back') }}</span>
                    </div>
                    <div class="img-box" v-if="detailData.livenessVerification">
                      <img :src="detailData.livenessVerification" @click="handlePreview(detailData.livenessVerification)" />
                      <span>{{ $t('platformCollection.liveness_verification') }}</span>
                    </div>
                    <span v-if="!detailData.idCardFront && !detailData.idCardBack && !detailData.livenessVerification">-</span>
                  </div>
                </div>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.email') }}:</span>
                <span class="value">{{ detailData.email || '-' }}</span>
              </a-col>

              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.bank_name') }}:</span>
                <span class="value">{{ detailData.bankName || '-' }}</span>
              </a-col>
              <a-col :span="6">
                <span class="label">{{ $t('platformCollection.bank_account') }}:</span>
                <span class="value">{{ detailData.bankCardNo || '-' }}</span>
              </a-col>
              
            </a-row>
          </div>
        </div>

        <!-- Risk assessment details -->
        <div class="section">
          <div class="section-title">{{ $t('platformCollection.risk_assessment_details') }}</div>
          <div class="section-content">
            <div class="risk-info">
              <div class="risk-level">
                {{ $t('platformCollection.risk_preference') }}:{{ detailData.level || '-' }}
              </div>
              <div
                class="qa-container"
                v-if="detailData.questionAndAnswerList && detailData.questionAndAnswerList.length"
              >
                <div class="qa-item" v-for="(item, index) in detailData.questionAndAnswerList" :key="index">
                  <div class="qa-question">{{ $t('platformCollection.question') }}{{ index + 1 }}:{{ parseQuestion(item) }}</div>
                  <div class="qa-answer">{{ $t('platformCollection.answer') }}:{{ parseAnswer(item) }}</div>
                </div>
              </div>
              <div v-else class="no-data">{{ $t('platformCollection.no_assessment_data') }}</div>
            </div>
          </div>
        </div>
      </div>
    </a-spin>

    <!-- Image preview modal -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false">
      <img alt="example" style="width: 100%" :src="previewImage" />
    </a-modal>
  </a-modal>
</template>

<script>
import { getInvestorDetail } from '@/webpublicapi/vietnamBackstage'

export default {
  name: 'InvestorDetailModal',
  data() {
    return {
      visible: false,
      loading: false,
      detailData: null,
      previewVisible: false,
      previewImage: '',
    }
  },
  methods: {
    async show(record) {
      this.visible = true
      this.loading = true
      this.detailData = null
      try {
        const res = await getInvestorDetail({ investUserId: record.id })
        if (res.success && res.respData) {
          this.detailData = res.respData
          this.detailData.id = record.id
          this.detailData.name = record.name
          this.detailData.mobile = record.mobile
          this.detailData.status = record.status
          this.detailData.registerTime = record.registerTime
          this.detailData.sex = record.sex
          this.detailData.resident = record.resident || '-'
          
        } else {
          this.$message.warning(res.respMsg || this.$t('platformCollection.get_details_failed'))
        }
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('platformCollection.system_error'))
      } finally {
        this.loading = false
      }
    },
    handleCancel() {
      this.visible = false
    },
    handlePreview(imgUrl) {
      this.previewImage = imgUrl
      this.previewVisible = true
    },
    parseQuestion(item) {
      const idx = item.indexOf(':')
      return idx > -1 ? item.substring(0, idx) : item
    },
    parseAnswer(item) {
      const idx = item.indexOf(':')
      return idx > -1 ? item.substring(idx + 1) : ''
    },
    formatGender(value) {
      if (value === 1 || value === '1') return this.$t('platformCollection.male')
      if (value === 2 || value === '2') return this.$t('platformCollection.female')
      return value || '-'
    },
  },
}
</script>

<style lang="less" scoped>
.detail-container {
  .section {
    margin-bottom: 24px;

    .section-title {
      background: #e6f7ff;
      padding: 8px 16px;
      font-weight: bold;
      color: #333;
      margin-bottom: 16px;
    }

    .section-content {
      padding: 0 16px;

      .label {
        color: #333;
        font-weight: 500;
      }

      .value {
        color: #666;
      }

      .img-wrapper {
        display: flex;
        align-items: flex-start;

        .img-box-container {
          display: flex;
          gap: 16px;
          margin-left: 8px;

          .img-box {
            display: flex;
            flex-direction: column;
            align-items: center;

            img {
              width: 80px;
              height: 48px;
              object-fit: cover;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
              cursor: pointer;
              background: #f5f5f5;
              margin-bottom: 4px;
            }

            span {
              font-size: 12px;
              color: #999;
            }
          }
        }
      }
    }
  }

  .risk-info {
    .risk-level {
      font-weight: 500;
      margin-bottom: 12px;
      color: #333;
    }

    .qa-container {
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      padding: 16px;
      background: #fafafa;

      .qa-item {
        margin-bottom: 12px;
        color: #333;

        .qa-question {
          font-weight: 500;
          margin-bottom: 4px;
        }

        .qa-answer {
          color: #666;
          padding-left: 16px;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .no-data {
      color: #999;
      text-align: center;
      padding: 20px;
    }
  }
}
</style>
